'use client'

/**
 * AshraryaProvider — the host app's contract with the bot.
 *
 * Every host app wraps its tree with this provider exactly ONCE (usually in
 * the root layout). It accepts a `config` object that declares:
 *
 *   - WHICH app this is        (appNamespace — backend + corpus filter key)
 *   - WHAT it should be called (appName — shown to the user)
 *   - WHERE to send questions  (webhookUrl — n8n entry point)
 *   - WHO is asking            (user — id/name/role for personalisation)
 *   - HOW it should look       (theme — branding)
 *   - WHERE NOT to show        (hideOnPaths — e.g. login screens)
 *
 * The config is validated on mount. Misconfigured providers print a clear
 * console error and DISABLE the widget rather than fail silently — surfacing
 * config drift early is cheaper than chasing data-leak bugs later.
 */

import { createContext, useContext, useMemo, useCallback, useState, useEffect, useRef } from 'react'
import { TourEngineProvider } from './components/TourEngine'

const AshraryaContext = createContext(null)
const TourStatusContext = createContext(null)

const TOUR_STATUS_PREFIX = 'ashraya:msg-tour-status:'

function loadStatuses(key) {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(key) : null
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch { return {} }
}

function saveStatuses(key, obj) {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, JSON.stringify(obj))
    }
  } catch {}
}

export function AshraryaProvider({ config, children }) {
  // Validate the bare minimum. We DO NOT throw — a misconfigured widget
  // should degrade gracefully (the host app continues working) and log a
  // very loud warning so devs notice during integration.
  const validatedConfig = useMemo(() => {
    if (!config || typeof config !== 'object') {
      console.error(
        '[Aumonline-Ashraya] Provider mounted WITHOUT a config object. ' +
        'Widget disabled. Pass config={{ appNamespace, appName, webhookUrl, user }}.'
      )
      return null
    }
    const required = ['appNamespace', 'appName', 'webhookUrl']
    const missing = required.filter((k) => !config[k])
    if (missing.length > 0) {
      console.error(
        `[Aumonline-Ashraya] Provider missing required config keys: ${missing.join(', ')}. ` +
        'Widget disabled to prevent cross-app data leakage.'
      )
      return null
    }
    // Normalise the namespace — lowercase, no whitespace. Prevents accidental
    // "kreta " vs "kreta" mismatches between host config and corpus.
    return {
      ...config,
      appNamespace: String(config.appNamespace).trim().toLowerCase(),
      theme: {
        primary: '#f97316',
        ...config.theme,
      },
      hideOnPaths: Array.isArray(config.hideOnPaths) ? config.hideOnPaths : [],
    }
  }, [config])

  // Telemetry sink — debug-only console output until the events webhook lands.
  const handleTelemetry = useCallback((event) => {
    if (validatedConfig?.debug) {
      // eslint-disable-next-line no-console
      console.log('[Ashraya event]', event)
    }
  }, [validatedConfig?.debug])

  // Per-message tour status: { [msgId]: 'started' | 'completed' | 'abandoned' }.
  const statusKey = useMemo(() => (
    validatedConfig
      ? `${TOUR_STATUS_PREFIX}${validatedConfig.appNamespace}:${String(validatedConfig.user?.id || 'anon')}`
      : null
  ), [validatedConfig])

  const [statuses, setStatuses] = useState({})

  // Track the message ID whose tour is currently live so that when the tour
  // ends we know which message to mark completed/abandoned.
  const activeMsgIdRef = useRef(null)

  useEffect(() => {
    if (!statusKey) return
    setStatuses(loadStatuses(statusKey))
  }, [statusKey])

  const writeStatus = useCallback((msgId, status) => {
    if (!msgId || !status || !statusKey) return
    setStatuses((prev) => {
      if (prev[msgId] === status) return prev
      const next = { ...prev, [msgId]: status }
      saveStatuses(statusKey, next)
      return next
    })
  }, [statusKey])

  const markMessageStarted = useCallback((msgId) => {
    if (!msgId) return
    activeMsgIdRef.current = msgId
    writeStatus(msgId, 'started')
  }, [writeStatus])

  // Subscribe to tour lifecycle events at the provider level so events fired
  // while the ChatPanel is unmounted (which happens during every tour) are
  // not lost. On terminal events, wipe the conversation so the next open
  // shows the default welcome.
  useEffect(() => {
    if (!statusKey || typeof window === 'undefined') return
    const ns = validatedConfig?.appNamespace
    const uid = String(validatedConfig?.user?.id || 'anon')
    const sessionKey = ns ? `ashraya:session:${ns}:${uid}` : null
    const historyKey = ns ? `ashraya:history:${ns}:${uid}` : null

    const handler = (evt) => {
      const detail = evt?.detail
      if (!detail) return
      if (detail.type === 'tour_completed' || detail.type === 'tour_abandoned') {
        const msgId = activeMsgIdRef.current
        if (msgId) {
          writeStatus(msgId, detail.type === 'tour_completed' ? 'completed' : 'abandoned')
          activeMsgIdRef.current = null
        }
        setTimeout(() => {
          try {
            if (sessionKey) window.localStorage.removeItem(sessionKey)
            if (historyKey) window.localStorage.removeItem(historyKey)
            if (statusKey) window.localStorage.removeItem(statusKey)
          } catch {}
          setStatuses({})
          try {
            window.dispatchEvent(new CustomEvent('ashraya:session-reset'))
          } catch {}
        }, 300)
      }
    }
    window.addEventListener('ashraya:tour', handler)
    return () => window.removeEventListener('ashraya:tour', handler)
  }, [statusKey, writeStatus, validatedConfig])

  const tourStatusValue = useMemo(() => ({
    statuses,
    markMessageStarted,
  }), [statuses, markMessageStarted])

  // If config is invalid we still render children so the host app keeps
  // working — we just skip the TourEngineProvider.
  if (!validatedConfig) {
    return (
      <AshraryaContext.Provider value={null}>
        {children}
      </AshraryaContext.Provider>
    )
  }

  return (
    <AshraryaContext.Provider value={validatedConfig}>
      <TourStatusContext.Provider value={tourStatusValue}>
        <TourEngineProvider
          theme={validatedConfig.theme}
          onEvent={handleTelemetry}
          userRole={validatedConfig.user?.role || null}
        >
          {children}
        </TourEngineProvider>
      </TourStatusContext.Provider>
    </AshraryaContext.Provider>
  )
}

/**
 * useAshraryaConfig — internal hook used by widget components.
 * Returns `null` when the provider is missing or misconfigured.
 */
export function useAshraryaConfig() {
  return useContext(AshraryaContext)
}

/**
 * useAshraryaTourStatus — returns { statuses, markMessageStarted }.
 */
export function useAshraryaTourStatus() {
  return useContext(TourStatusContext) || { statuses: {}, markMessageStarted: () => {} }
}
